<script setup lang="ts">
  // Shared body for the migrated series-based stats pages: colored group
  // toggles + chart card + sortable table with pinned totals + export. Pages
  // pass an IStats (date buckets) or IEntityStats (one row per entity) whose
  // group titles/colors and chart labels are already prepared.
  import { computed, getCurrentInstance, ref } from 'vue';
  import ChartWidget from '@/components/widgets/ChartWidget.vue';
  import V2StatsChartCard from './V2StatsChartCard.vue';
  import V2StatsExportButton from './V2StatsExportButton.vue';
  import V2StatsTable from './V2StatsTable.vue';
  import { IV2StatsTableColumn, IV2StatsTableRow } from './v2-stats-table.types';
  import { IStatsExportColumn } from '@/domain/stats/stats-export';
  import { IEntityStats } from '@/interfaces/IEntityStats';
  import { IStats } from '@/interfaces/IStats';
  import { statDateFormatted } from '@/statsutils';
  import { displayPrice, padNumber, round2 } from '@/utils';

  interface INamedEntity {
    name?: string | null;
  }

  const props = withDefaults(
    defineProps<{
      stats: IStats | IEntityStats<INamedEntity> | null;
      loading?: boolean;
      chartType?: 'line' | 'bar' | 'stackedBar';
      chartTitle: string;
      currencySymbol: string;
      /** Base filename (no extension) for XLSX/CSV exports. */
      exportFilename: string;
      exportSheet: string;
      /** Translated first-column header (Fecha / Producto / …). */
      firstColumnLabel: string;
      defaultSort?: { key: string; desc?: boolean } | null;
      /** Hides the pinned totals row (e.g. mixed-unit stock table). */
      hideTotals?: boolean;
      /** Caps how many buckets/entities the chart renders (table shows all). */
      chartLimit?: number;
    }>(),
    {
      loading: false,
      chartType: 'line',
      defaultSort: null,
      hideTotals: false,
      chartLimit: 0,
    }
  );

  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const isEntityMode = computed(
    () => !!props.stats && 'entities' in props.stats
  );

  const entities = computed<{ [id: number]: INamedEntity }>(() =>
    props.stats && 'entities' in props.stats ? props.stats.entities : {}
  );

  //#region Group visibility toggles

  /** Hidden (not visible) ids so new datasets default to all-visible. */
  const hiddenGroupIds = ref<Set<string>>(new Set());

  function isGroupVisible(id: string): boolean {
    return !hiddenGroupIds.value.has(id);
  }

  function toggleGroup(id: string) {
    const next = new Set(hiddenGroupIds.value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // Never allow hiding the last visible group (empty chart is confusing).
    const groups = props.stats?.groups ?? [];
    if (groups.every((group) => next.has(group.id))) return;
    hiddenGroupIds.value = next;
  }

  //#endregion

  //#region Chart

  const chartStats = computed<IStats | null>(() => {
    if (!props.stats) return null;
    const limit = props.chartLimit > 0 ? props.chartLimit : undefined;
    const groups = props.stats.groups
      .filter((group) => isGroupVisible(group.id))
      .map((group) =>
        limit ? { ...group, stats: group.stats.slice(0, limit) } : group
      );
    return {
      interval: props.stats.interval,
      labels: limit
        ? (props.stats.labels ?? []).slice(0, limit)
        : props.stats.labels,
      groups,
    };
  });

  //#endregion

  //#region Table

  function formatValue(type: 'number' | 'currency', value: unknown): string {
    if (value === null || typeof value === 'undefined') return '';
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    return type === 'currency'
      ? `${props.currencySymbol}${displayPrice(num)}`
      : String(round2(num));
  }

  const columns = computed<IV2StatsTableColumn[]>(() => {
    const groups = props.stats?.groups ?? [];
    return [
      {
        key: 'sortKey',
        label: props.firstColumnLabel,
        numeric: false,
        format: (value, row) => String(row.label ?? ''),
      },
      ...groups.map(
        (group): IV2StatsTableColumn => ({
          key: group.id,
          label: group.title,
          numeric: true,
          format: (value) => formatValue(group.type, value),
        })
      ),
    ];
  });

  const rows = computed<IV2StatsTableRow[]>(() => {
    const stats = props.stats;
    if (!stats || !stats.groups.length) return [];
    const groups = stats.groups;
    const dataLength = groups[0].stats.length;
    const result: IV2StatsTableRow[] = [];

    for (let i = 0; i < dataLength; i++) {
      const first = groups[0].stats[i];
      let row: IV2StatsTableRow;
      if (isEntityMode.value) {
        const entity = first.id != null ? entities.value[first.id] : undefined;
        const label =
          entity?.name ?? i18nT('v2Stats.seriesReport.deletedEntity');
        row = { id: `e-${first.id ?? i}`, entityId: first.id, sortKey: label, label };
      } else {
        const sortKey = `${first.date ?? ''}${
          typeof first.hour !== 'undefined' ? padNumber(first.hour, 2) : ''
        }`;
        row = { id: `d-${sortKey}`, sortKey, label: statDateFormatted(first) };
      }
      for (const group of groups) {
        row[group.id] = group.stats[i]?.value ?? 0;
      }
      result.push(row);
    }
    return result;
  });

  const totals = computed<Record<string, number> | null>(() => {
    if (props.hideTotals) return null;
    const stats = props.stats;
    if (!stats || !stats.groups.length) return null;
    const dataLength = stats.groups[0].stats.length;
    if (!dataLength) return null;
    const result: Record<string, number> = {};
    for (const group of stats.groups) {
      let sum = 0;
      for (const stat of group.stats) sum += stat.value ?? 0;
      // Averages are re-averaged, not summed (matches the legacy tables).
      result[group.id] = round2(
        group.id === 'average' ? sum / dataLength : sum
      );
    }
    return result;
  });

  //#endregion

  //#region Export

  const exportColumns = computed<IStatsExportColumn[]>(() => [
    { key: 'label', header: props.firstColumnLabel },
    ...(props.stats?.groups ?? []).map(
      (group): IStatsExportColumn => ({
        key: group.id,
        header: group.title,
        type: 'number',
      })
    ),
  ]);

  const exportRows = computed<Array<Record<string, unknown>>>(() =>
    rows.value.map((row) => ({ ...row }))
  );

  //#endregion
</script>

<template>
  <div class="v2-stats-series-report">
    <div
      v-if="stats && stats.groups.length > 1"
      class="series-toggles"
    >
      <button
        v-for="group in stats.groups"
        :key="group.id"
        type="button"
        class="series-toggle"
        :class="{ 'is-active': isGroupVisible(group.id) }"
        :style="
          isGroupVisible(group.id)
            ? {
                borderColor: group.color,
                backgroundColor: (group.color || '') + '22',
              }
            : undefined
        "
        @click="toggleGroup(group.id)"
      >
        <span
          class="toggle-dot"
          :style="{ backgroundColor: group.color }"
        />
        {{ group.title }}
      </button>
    </div>

    <V2StatsChartCard
      :title="chartTitle"
      :loading="loading"
      :empty="!loading && (!chartStats || !chartStats.groups.length)"
    >
      <template #actions>
        <V2StatsExportButton
          :filename="exportFilename"
          :sheet-name="exportSheet"
          :columns="exportColumns"
          :rows="exportRows"
          :disabled="loading"
        />
      </template>
      <ChartWidget
        v-if="chartStats && chartStats.groups.length"
        :stats="chartStats"
        :type="chartType"
        :value-prefix="currencySymbol"
      />
    </V2StatsChartCard>

    <V2StatsTable
      class="series-table"
      :columns="columns"
      :rows="rows"
      :totals="totals"
      :loading="loading"
      :default-sort="defaultSort"
      sticky-first
    >
      <template #cell-sortKey="{ row }">
        <slot name="first-cell" :row="row">
          <span class="series-first-label">{{ String(row.label ?? '') }}</span>
        </slot>
      </template>
    </V2StatsTable>
  </div>
</template>

<style scoped lang="scss">
  .v2-stats-series-report {
    .series-toggles {
      @apply tw-mb-3 tw-flex tw-flex-wrap tw-items-center;

      .series-toggle {
        // margins instead of flex gap: unsupported on Chrome <84.
        @apply tw-mb-1.5 tw-mr-1.5 tw-inline-flex tw-h-11 tw-items-center tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors;

        &.is-active {
          @apply tw-text-neutral-900;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }

        .toggle-dot {
          @apply tw-mr-1.5 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-neutral-300;
        }
      }
    }

    .series-table {
      @apply tw-mt-4;

      .series-first-label {
        // Inside the capped mobile label column: clamp instead of overflowing.
        @media (max-width: 767.98px) {
          @apply tw-min-w-0 tw-overflow-hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          word-break: break-word; // fallback for Chrome <80
          overflow-wrap: anywhere;
        }
      }
    }
  }
</style>
